import request from '../../client';

import {
  getActiveCart,
  getGuaranteeItems,
  addPromoCode,
  removePromoCode,
  getTaxEstimation,
} from './cart.migrated';

const addLineItems = ({ lineItems, cartId }) =>
  request({
    uri: 'carts/line-items',
    options: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        lineItems.map(({ sku, quantity, warrantyPlanId }) => ({
          sku,
          quantity,
          warrantyPlanId,
          cartId,
        })),
      ),
    },
  });

const removeLineItem = async ({ lineItemId, cartId }) => {
  try {
    return await request({
      uri: `carts/line-items/${lineItemId}`,
      options: {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cartId }),
      },
    });
  } catch (error) {
    return false;
  }
};

const changeLineItemQuantity = async ({ lineItemId, quantity, cartId }) => {
  try {
    return await request({
      uri: `carts/line-items/${lineItemId}/quantity`,
      options: {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity, cartId }),
      },
    });
  } catch (error) {
    return false;
  }
};

const setShippingAddress = ({ shippingAddress, cartId }) =>
  request({
    uri: 'carts/shipping-address',
    options: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...shippingAddress, cartId }),
    },
  });

const setBillingAddress = ({ billingAddress, cartId }) =>
  request({
    uri: 'carts/billing-address',
    options: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...billingAddress,
        streetAddress:
          billingAddress.streetAddress || billingAddress.streetName,
        cartId,
      }),
    },
  });

const removeBillingAddress = ({ cartId }) =>
  request({
    uri: 'carts/billing-address',
    options: {
      method: 'DELETE',
    },
    body: JSON.stringify({ cartId }),
  });

const updateVariantLineItem = async ({ lineItemId, sku, cartId }) => {
  try {
    return await request({
      uri: `carts/line-items/${lineItemId}/variant`,
      options: {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sku, cartId }),
      },
    });
  } catch (error) {
    return false;
  }
};

const getVariantOptions = async (productId) => {
  try {
    return await request({
      uri: `carts/product/${productId}/variants`,
      options: {
        method: 'GET',
      },
    });
  } catch (error) {
    return false;
  }
};

const validateAddress = async (addressWithCartId) => {
  try {
    return await request({
      uri: 'carts/address/validate',
      options: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(addressWithCartId),
      },
    });
  } catch (e) {
    return false;
  }
};

const getShippingMethods = ({
  overwritePaidInHomeSetupShipping = false,
  customInHomeSetupPricing = false,
  waiveAkHiShipping = false,
}) => request({
    uri: 'carts/shipping-methods',
    options: {
      method: 'GET',
      headers: {
        ...(overwritePaidInHomeSetupShipping
          ? { 'x-overwrite-paid-in-home-setup-shipping-ab-test': true }
          : {}),
        ...(customInHomeSetupPricing || customInHomeSetupPricing === 0
          ? {
              'x-custom-in-home-setup-pricing-ab-test':
                customInHomeSetupPricing,
            }
          : {}),
        ...(waiveAkHiShipping ? { 'x-waive-ak-hi-shipping': true } : {}),
      },
    },
  });

const getWarrantyPlans = () =>
  request({
    uri: 'carts/warranty-plans',
    options: {
      method: 'GET',
    },
  });

const setShippingMethods = ({
  selectedShippingMethods,
  overwritePaidInHomeSetupShipping = false,
  customInHomeSetupPricing = null,
  waiveAkHiShipping = false,
  cartId,
}) => request({
    uri: 'carts/shipping-methods',
    options: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(overwritePaidInHomeSetupShipping
          ? { 'x-overwrite-paid-in-home-setup-shipping-ab-test': true }
          : {}),
        ...(typeof customInHomeSetupPricing === 'number' && customInHomeSetupPricing >= 0
          ? {
              'x-custom-in-home-setup-pricing-ab-test':
                customInHomeSetupPricing,
            }
          : {}),
        ...(waiveAkHiShipping ? { 'x-waive-ak-hi-shipping': true } : {}),
      },
      body: JSON.stringify({ ...selectedShippingMethods, cartId }),
    },
  });

const getCartAOVs = async () => {
  try {
    return await request({
      uri: 'carts/aovs',
      options: {
        method: 'GET',
      },
    });
  } catch (e) {
    return false;
  }
};

const getPromoPrice = async ({ sku, quantity, ignoredSKU }) => {
  try {
    return await request({
      // eslint-disable-next-line max-len
      uri: `carts/${sku}/promo-price?ignoredSKU=${ignoredSKU}&quantity=${quantity}`,
      options: {
        method: 'GET',
      },
    });
  } catch (e) {
    return false;
  }
};

const addGiftCard = ({ giftCardNumber, securityCode, cartId }) =>
  request({
    uri: 'carts/gift-card',
    options: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ giftCardNumber, securityCode, cartId }),
    },
  });

const removeGiftCard = ({ giftCardCode, cartId }) =>
  request({
    uri: `carts/gift-card/${giftCardCode}`,
    options: {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
      body: JSON.stringify({ cartId }),
    },
  });

const giftCardBalance = ({ giftCardNumber, securityCode, cartId }) =>
  request({
    uri: 'carts/gift-card-balance',
    options: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ giftCardNumber, securityCode, cartId }),
    },
  });

const removeGiftCardsFromCart = ({ cartId }) =>
  request({
    uri: 'carts/gift-card',
    options: {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cartId }),
    },
  });

const addWarrantyPlanToLineItem = ({ lineItemId, warrantyPlanId, cartId }) =>
  request({
    uri: `carts/line-items/${lineItemId}/warranty-plan`,
    options: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ warrantyPlanId, cartId }),
    },
  });
const getAbandonedCartToasts = async () => {
  const { included } = await request({
    uri: `block_content/abandon_cart_messages?include=field_abandon_cart_message`,
    host: window.drupalSettings.pur_api.drupalBase,
    options: {
      method: 'GET',
    },
  });
  return included;
};

const getAvailableDiscountCodeForAbandonedCarts = async (queryParams) => {
  const response = await request({
    host: process.env.ABANDONED_CARTS_COUPON_CODES_LAMBDA_URL,
    uri: `active-coupons${queryParams ?? ''}`,
    options: {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  });
  return JSON.parse(response?.response ?? {});
};

const getAvailableDiscountCodeForAbandonedCheckout = async (queryParams) => {
  const response = await request({
    host: process.env.ABANDONED_CARTS_COUPON_CODES_LAMBDA_URL,
    uri: `active-coupons${queryParams ?? ''}`,
    options: {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  });
  return JSON.parse(response?.response ?? {});
};

const getProductPopularity = ({ productKey }) =>
  request({
    uri: `carts/product-popularity/${productKey}`,
    options: {
      method: 'GET',
    },
  });

const checkCartCouponCodeValidity = async (cartId, couponCodes) =>
  request({
    uri: `carts/${cartId}/validate-cart-coupons`,
    options: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ couponCodes }),
    },
  });

const cartService = {
  getActiveCart,
  getTaxEstimation,
  getVariantOptions,
  addLineItems,
  removeLineItem,
  changeLineItemQuantity,
  updateVariantLineItem,
  setBillingAddress,
  removeBillingAddress,
  setShippingAddress,
  validateAddress,
  getShippingMethods,
  getCartAOVs,
  getPromoPrice,
  setShippingMethods,
  addPromoCode,
  removePromoCode,
  addGiftCard,
  removeGiftCard,
  giftCardBalance,
  removeGiftCardsFromCart,
  getWarrantyPlans,
  addWarrantyPlanToLineItem,
  getAbandonedCartToasts,
  getAvailableDiscountCodeForAbandonedCarts,
  getAvailableDiscountCodeForAbandonedCheckout,
  getProductPopularity,
  getGuaranteeItems,
  checkCartCouponCodeValidity,
};

export default cartService;
